<template>
    <div class="card mb-1 compact-price-row">
        <div class="card-body p-2">
            <div class="row align-items-center">
                <div class="col-12 col-sm-2 retailer-logo d-flex align-items-center">
                    <a :href="`/retailers/${price.retailer.id}/${price.retailer.slug}`" class="retailer-logo-link">
                        <img v-if="price.retailer.logo" :src="retailerLogoUrl(price.retailer.logo)" :alt="price.retailer.name || 'Forhandler'" class="retailer-logo-img" />
                        <span v-else class="retailer-name-fallback">{{ price.retailer.name }}</span>
                    </a>
                    <button class="btn btn-link p-0 ms-2" type="button" data-bs-toggle="collapse" :data-bs-target="`#collapse-${index}`" aria-expanded="false" :aria-controls="`collapse-${index}`">
                        <font-awesome-icon icon="fa-solid fa-chevron-down" />
                    </button>
                </div>
                <div class="col-12 col-sm-2 stock-info">
                    <p v-if="price.stock_info == 'In stock'" class="text-success mb-0">
                        <font-awesome-icon v-tooltip="`På lager`" icon="fa-solid fa-circle-check" /> På lager
                    </p>
                    <p v-else class="text-danger mb-0">
                        <font-awesome-icon icon="fa-solid fa-circle-xmark" v-tooltip="`Ikke på lager`" /> Ikke på lager
                    </p>
                </div>
                <div class="col-12 col-sm-2 lead-time">
                    <span class="small">{{ leveringstid(price) }}</span>
                </div>
                <div class="col-12 col-sm-2 price-info text-end">
                    <h5 class="text-primary mb-0">{{ numberFormat(price.price_with_shipping_min) }}</h5>
                    <small class="text-muted">INKL. FRAGT</small>
                </div>
                <div class="col-12 col-sm-2 price-breakdown text-end">
                    <div class="d-flex flex-column align-items-end">
                        <div>
                            <small class="text-muted ms-1 text-uppercase">PRODUKT </small>
                            <h6 class="text-secondary mb-0 d-inline fw-bold text-primary">{{ numberFormat(price.price) }}</h6>
                        </div>
                        <div class="mt-1">
                            <small class="text-muted ms-1 text-uppercase">FRAGT </small>
                            <h6 class="text-secondary mb-0 d-inline fw-bold text-success">{{ fragt(price) }}</h6>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-2 action-link text-end">
                    <a target="_blank" rel="sponsored noopener nofollow" :href="`/redirect/${productId}/${price.price_guid}`" class="btn btn-sm btn-pricecompare-primary">Gå til butik</a>
                </div>
            </div>
            <div :id="`collapse-${index}`" class="collapse">
                <hr class="mt-2 mb-0" style="border-color: var(--pc-border, rgba(255,255,255,0.1));" />
                <div class="retailer-expand">
                    <!-- Row 1: Description full width -->
                    <div class="expand-about" v-if="price.retailer.description">
                        <p class="expand-text">{{ price.retailer.description.length > 200 ? price.retailer.description.substring(0, 200) + '...' : price.retailer.description }}</p>
                    </div>

                    <!-- Row 2: 4-column grid — Betaling | Forsendelse | Certificeret | Kontakt -->
                    <div class="expand-grid">
                        <div class="expand-col" v-if="price.retailer.payment_options && price.retailer.payment_options.length">
                            <span class="expand-col-label">Betaling</span>
                            <div class="icon-row">
                                <span v-for="opt in price.retailer.payment_options" :key="opt.id" class="icon-chip" v-tooltip="opt.payment_name">
                                    <img v-if="opt.payment_logo_image" :src="opt.payment_logo_image" :alt="opt.payment_name" />
                                    <span v-else>{{ opt.payment_name }}</span>
                                </span>
                            </div>
                        </div>

                        <div class="expand-col" v-if="price.retailer.carrier_options && price.retailer.carrier_options.length">
                            <span class="expand-col-label">Forsendelse</span>
                            <div class="icon-row">
                                <span v-for="opt in price.retailer.carrier_options" :key="opt.id" class="icon-chip icon-chip-dark" v-tooltip="opt.carrier_name">
                                    <img v-if="opt.carrier_logo_image" :src="opt.carrier_logo_image" :alt="opt.carrier_name" />
                                    <span v-else>{{ opt.carrier_name }}</span>
                                </span>
                            </div>
                        </div>

                        <div class="expand-col" v-if="hasTrustMarks">
                            <span class="expand-col-label">Certificeret</span>
                            <div class="icon-row">
                                <span v-if="price.retailer.has_mark_emaerket" class="icon-chip icon-chip-dark" v-tooltip="'e-mærket — certificeret af e-handelsfonden'">
                                    <img src="/images/trustmarks/emaerket.svg" alt="e-mærket" />
                                </span>
                                <span v-if="price.retailer.has_mark_emaerket_plus" class="icon-chip icon-chip-dark" v-tooltip="'e-mærket+ — udvidet certificering'">
                                    <img src="/images/trustmarks/emaerket-plus.svg" alt="e-mærket+" />
                                </span>
                                <span v-if="price.retailer.has_mark_miljoepakning" class="icon-chip icon-chip-dark" v-tooltip="'Miljøvenlig emballage'">
                                    <img src="/images/trustmarks/miljoepakning.svg" alt="Miljøpakning" />
                                </span>
                                <span v-if="price.retailer.has_mark_ingenco2" class="icon-chip icon-chip-dark" v-tooltip="'CO₂-neutral forsendelse'">
                                    <img src="/images/trustmarks/ingenco2.svg" alt="CO₂-neutral" />
                                </span>
                                <span v-if="price.retailer.has_mark_csr" class="icon-chip icon-chip-dark" v-tooltip="'Corporate Social Responsibility'">
                                    <img src="/images/trustmarks/csr.svg" alt="CSR" />
                                </span>
                            </div>
                        </div>

                        <div class="expand-col expand-col-contact">
                            <span class="expand-col-label">Kontakt</span>
                            <div class="contact-lines">
                                <small v-if="price.retailer.phone"><i class="bi bi-telephone"></i> {{ price.retailer.phone }}</small>
                                <small v-if="price.retailer.email"><i class="bi bi-envelope"></i> {{ price.retailer.email }}</small>
                                <Link :href="`/retailers/${price.retailer.id}/${price.retailer.slug}`" class="expand-link">
                                    <i class="bi bi-shop"></i> Se forhandler
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import { retailerLogoUrl } from '@/Utils/retailerLogo';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    price: {
        type: Object,
        required: true
    },
    index: {
        type: Number,
        required: true
    },
    productId: {
        type: String,
        required: true
    }
});

const hasTrustMarks = computed(() => {
    const r = props.price.retailer;
    return r.has_mark_emaerket || r.has_mark_emaerket_plus || r.has_mark_miljoepakning || r.has_mark_ingenco2 || r.has_mark_csr;
});

function numberFormat(value) {
    value = Number(value);
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

/**
 * "1 dag" eller "2 dage", ikke "1 dag(e)". Parentesen er en programmørs måde
 * at slippe for at bøje ordet, og den ser doven ud på en side hvor vi beder
 * folk om at stole på os.
 */
function leveringstid(price) {
    const dage = Math.round(Number(price.lead_time_calculated_first));

    if (!Number.isFinite(dage) || dage <= 0) {
        return 'Ukendt leveringstid';
    }

    return dage === 1 ? '1 dag' : `${dage} dage`;
}

/** Nul kroner i fragt er en oplysning værd at fremhæve, ikke "0,00 kr.". */
function fragt(price) {
    const beløb = Number(price.lowest_shipping_cost);

    if (!Number.isFinite(beløb) || beløb <= 0) {
        return 'Gratis';
    }

    return numberFormat(beløb);
}
</script>

<style scoped>
.compact-price-row {
    padding: 0.5rem;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
}

.retailer-logo-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 48px;
    background: #fff;
    border-radius: 6px;
    padding: 6px 10px;
    border: 1px solid #e9ecef;
}

.retailer-logo-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.retailer-name-fallback {
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.stock-info {
    font-size: 0.9rem;
}

.lead-time {
    font-size: 0.8rem;
    color: #6c757d;
}

.price-info {
    text-align: right;
}

.price-info h5 {
    font-size: 1rem;
    margin-bottom: 0;
}

.price-breakdown {
    text-align: right;
}

.price-breakdown h6 {
    font-size: 0.9rem;
    margin-bottom: 0;
}

.action-link .btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
}

.retailer-expand {
    padding: 12px 0 4px;
}

.expand-about {
    margin-bottom: 12px;
}

.expand-text {
    font-size: 0.8rem;
    color: var(--pc-text-secondary, #adb5bd);
    line-height: 1.5;
    margin: 0;
}

.expand-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 20px;
    align-items: start;
}

.expand-col {
    min-width: 0;
}

.expand-col-label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--pc-text-muted, #6c757d);
    font-weight: 600;
    margin-bottom: 8px;
    border-bottom: 1px solid var(--pc-border, rgba(255, 255, 255, 0.08));
    padding-bottom: 4px;
}

.icon-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.icon-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 4px 8px;
    border-radius: 5px;
    background: #fff;
    cursor: help;
    overflow: hidden;
}

.icon-chip img {
    height: 22px;
    width: auto;
    object-fit: contain;
}

.icon-chip span {
    font-size: 0.65rem;
    color: #333;
}

.icon-chip-dark {
    background: transparent;
    padding: 0;
}

.icon-chip-dark img {
    height: 32px;
    width: auto;
    border-radius: 5px;
}

.expand-col-contact {
    text-align: right;
}

.expand-col-contact .expand-col-label {
    text-align: right;
}

.contact-lines {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.contact-lines small {
    font-size: 0.8rem;
    color: var(--pc-text-muted, #6c757d);
}

.contact-lines small i {
    margin-right: 4px;
}

.expand-link {
    font-size: 0.8rem;
    color: var(--pc-primary, #00c9a7);
    text-decoration: none;
    font-weight: 500;
}

.expand-link:hover {
    text-decoration: underline;
}

.expand-link i {
    margin-right: 4px;
}

@media (max-width: 767.98px) {
    .expand-grid {
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .expand-col-contact {
        text-align: left;
    }

    .expand-col-contact .expand-col-label {
        text-align: left;
    }

    .contact-lines {
        align-items: flex-start;
    }
}

@media (max-width: 575.98px) {
    .expand-grid {
        grid-template-columns: 1fr;
    }
}

.retailer-info {
    font-size: 0.9rem;
    color: #333;
}

.rating .badge {
    font-size: 0.9rem;
}

.payment-icon, .shipping-icon {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
    padding: 4px;
    background-color: #f8f9fa;
}
</style>